import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import DashboardLayout from "./DashboardLayout";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Custom arrow components
const NextArrow = ({ onClick }) => (
  <div style={{ ...arrowBase, right: 10 }} onClick={onClick}>▶</div>
);

const PrevArrow = ({ onClick }) => (
  <div style={{ ...arrowBase, left: 10 }} onClick={onClick}>◀</div>
);

const arrowBase = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 10,
  background: "white",
  borderRadius: "50%",
  width: "40px",
  height: "40px",
  fontSize: "24px",
  color: "#333",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
  cursor: "pointer",
};

function OrderDetails() {
  const { id } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubmission = async () => {
      try {
        const docRef = doc(db, "submissions", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setSubmission(docSnap.data());
        } else {
          console.error("No such document!");
        }
      } catch (error) {
        console.error("Error fetching submission:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubmission();
  }, [id]);

  if (loading) return <DashboardLayout><p>Loading...</p></DashboardLayout>;
  if (!submission) return <DashboardLayout><p>Submission not found.</p></DashboardLayout>;

  const {
    cardName,
    submissionId,
    status,
    createdAt,
    images,
    reviews,
  } = submission;

  const allImages = Object.entries(images || {});

  // ✅ Use the most recent review (last item)
  const latestReview =
    Array.isArray(reviews) && reviews.length > 0 ? reviews[reviews.length - 1] : undefined;

  const issuesList = Array.isArray(latestReview?.issues) ? latestReview.issues : [];

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  // Support both legacy numeric values and new string ranges
  const renderEstimatedValue = () => {
    const gv = latestReview?.gradedValue;
    if (gv === null || gv === undefined) return "N/A";
    if (typeof gv === "string") {
      const trimmed = gv.trim();
      return trimmed === "" ? "N/A" : trimmed; // show string as-is (may already include $ or range)
    }
    if (typeof gv === "number") {
      return `$${gv.toLocaleString()}`;
    }
    return "N/A";
  };

  return (
    <DashboardLayout>
      <div style={styles.centeredColumn}>
        <button onClick={() => navigate("/orders")} style={styles.backButton}>
          ← Back to Orders
        </button>
        <h2 style={styles.pageTitle}>Submission Details</h2>

        <div style={styles.centeredRow}>
          <div style={styles.carouselContainer}>
            {allImages.length > 0 ? (
              <Slider {...sliderSettings}>
                {allImages.map(([key, url]) => (
                  <div key={key} style={styles.slide}>
                    <img src={url} alt={key} style={styles.carouselImage} />
                    <p style={styles.caption}>
                      {key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())}
                    </p>
                  </div>
                ))}
              </Slider>
            ) : (
              <p><em>No images available</em></p>
            )}
          </div>

          <div style={styles.infoBlock}>
            <div style={styles.cardInfoBox}>
              <h3 style={styles.boxTitle}>Card Info</h3>
              <p><strong>Card Name:</strong> {cardName}</p>
              <p><strong>Submission ID:</strong> {submissionId}</p>
              <p><strong>Status:</strong> {status}</p>
              <p><strong>Date Submitted:</strong> {createdAt?.toDate ? createdAt.toDate().toLocaleString() : "N/A"}</p>
            </div>

            <div style={styles.feedbackBox}>
              <h3 style={styles.boxTitle}>Grader Feedback</h3>
              <p><strong>Expected Grade:</strong> {latestReview?.anticipatedGrade ?? "N/A"}</p>
              <p><strong>Ceiling Grade:</strong> {latestReview?.ceilingGrade ?? "N/A"}</p>
              <p><strong>Centering (Front):</strong> {latestReview?.centeringFront || "N/A"}</p>
              <p><strong>Centering (Back):</strong> {latestReview?.centeringBack || "N/A"}</p>
              <p><strong>Estimated Graded Value:</strong> {renderEstimatedValue()}</p>

              {/* Issues Cited section */}
              <div style={{ marginTop: "12px" }}>
                <p style={{ marginBottom: "6px" }}><strong>Issues Cited:</strong></p>
                {issuesList.length > 0 ? (
                  <ul style={{ paddingLeft: "20px", margin: 0 }}>
                    {issuesList.map((issue) => (
                      <li key={issue}>- {issue}</li>
                    ))}
                  </ul>
                ) : (
                  <p>None</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

const styles = {
  centeredColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "Montserrat, sans-serif",
  },
  centeredRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "40px",
    flexWrap: "wrap",
    marginTop: "30px",
  },
  backButton: {
    backgroundColor: "#1976d2",
    color: "#fff",
    fontWeight: "bold",
    fontFamily: "Montserrat, sans-serif",
    border: "none",
    borderRadius: "6px",
    padding: "10px 20px",
    marginBottom: "15px",
    cursor: "pointer",
    fontSize: "16px",
  },
  pageTitle: {
    fontWeight: "bold",
    fontFamily: "Montserrat, sans-serif",
    fontSize: "24px",
    marginBottom: "10px",
  },
  boxTitle: {
    fontSize: "21px",
    textAlign: "center",
    fontWeight: "bold",
    fontFamily: "Montserrat, sans-serif",
    marginBottom: "16px",
  },
  carouselContainer: {
    width: "500px",
    position: "relative",
    overflow: "visible",
  },
  slide: {
    textAlign: "center",
  },
  carouselImage: {
    width: "100%",
    maxHeight: "500px",
    objectFit: "contain",
    borderRadius: "8px",
    border: "1px solid #ccc",
  },
  caption: {
    fontSize: "14px",
    color: "#ccc",
    marginTop: "8px",
  },
  infoBlock: {
    display: "flex",
    flexDirection: "column",
    lineHeight: "1.6",
    textAlign: "left",
  },
  cardInfoBox: {
    width: "500px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "16px",
  },
  feedbackBox: {
    width: "500px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "40px",
  },
};

export default OrderDetails;
